import { CommonModule, DOCUMENT, } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';
import { AuthenticationService } from './service/authentication.service';
import { BodyZoomService } from './service/body-zoom.service';
import { SyncfusionGridLocaleService } from './service/syncfusion-grid-locale.service';
import { SyncfusionMenuZoomService } from './service/syncfusion-menu-zoom.service';
import { SyncfusionTooltipZoomService } from './service/syncfusion-tooltip-zoom.service';
import { SyncfusionGridDateFilterService } from './service/syncfusion-grid-date-filter.service';
import { SyncfusionGridFilterScopeService } from './service/syncfusion-grid-filter-scope.service';
import { SyncfusionGridActionColumnService } from './service/syncfusion-grid-action-column.service';
import { TawkService } from './service/tawk.service';
import { ManagedTabService } from './service/managed-tab.service';
import { GlobalLogoutService, LOGOUT_TAB_ID } from './service/global-logout.service';
import { SessionIdleWarningComponent } from './service/session-idle-warning.component';
import { SessionPolicyReadService } from './service/session-policy-read.service';
import { migrateLegacyCustomizationFlags } from './shared/customization-flags';

/**
 * Whether a location pathname is the email-verification (login entry) route.
 * @param pathname - `window.location.pathname` of the page (no query string or hash).
 * @returns `true` for `/emailVerification` with or without a trailing slash, otherwise `false`.
 */
export function isEmailVerificationPath(pathname: string): boolean {
  return pathname === '/emailVerification' || pathname === '/emailVerification/';
}

/**
 * Records in `sessionStorage` that the page is being unloaded, so the dynamic-filter screens
 * (`DynamicFiltersComponent`, `AssignmentReportsDynamicFilterComponent`) can reset their column
 * toggles after an ordinary refresh. The flag is deliberately NOT written on `/emailVerification`:
 * nothing there reads it, the page's own one-time reload clears storage anyway, and writing it
 * there left a stray session key behind right after an idle logout.
 * @param storage - The `sessionStorage` to write into.
 * @param pathname - `window.location.pathname` at the moment of the unload.
 * @returns void. Side effect: sets `isPageRefreshing` to `'true'` unless the route is `/emailVerification`.
 */
export function markPageRefreshing(storage: Pick<Storage, 'setItem'>, pathname: string): void {
  if (isEmailVerificationPath(pathname)) {
    return;
  }
  storage.setItem('isPageRefreshing', 'true');
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterModule, SessionIdleWarningComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  showContent = true;

  /** Debounce handle for the window-resize overlay repositioning pass. */
  private overlayRepositionTimer: ReturnType<typeof setTimeout> | null = null;

  /** Teardown for the Renderer2 window-resize listener; called on destroy. */
  private removeResizeListener: (() => void) | null = null;

  /** Teardown for the Renderer2 beforeunload listener; called on destroy. */
  private removeBeforeUnloadListener: (() => void) | null = null;

  showPasswordPopup = false;
  showSecurityQue = false;
  showSSOlogin = false;
  showResetPassword = false;
  showUnlockAcc = false;
  showSignIn = false;
  showOtpVerifyPopup = false;
  showQuestionsToAnswer = false;
  showOverdueCompliance = false;
  openHelpDesk = false;
  showActSummary = false;
  showLegalUpdateDetails = false;
  showUpdateDetails = false;
  showInternalOverdueCompliance = false;
  showLogin = false;
  showAuthRedirection = false;
  showResetLinkLoader = false;

  constructor(
    // Routing / navigation
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    // Auth / session
    private authenticationService: AuthenticationService,
    private managedTabService: ManagedTabService,
    private globalLogoutService: GlobalLogoutService,
    // D9: the passive startup policy read (POST /token/session-policy)
    private sessionPolicyRead: SessionPolicyReadService,
    // i18n
    private translateService: TranslateService,
    // Third-party widgets
    private tawkService: TawkService,
    // Layout / viewport
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private bodyZoom: BodyZoomService,
    private syncfusionMenuZoom: SyncfusionMenuZoomService,
    /** Installs the shim that keeps Syncfusion tooltips beside their target under the body zoom. */
    private syncfusionTooltipZoom: SyncfusionTooltipZoomService,
    private syncfusionGridLocale: SyncfusionGridLocaleService,
    /** Installs the shim that renders dates in grid filter popups as dd-MMM-yyyy. */
    private syncfusionGridDateFilter: SyncfusionGridDateFilterService,
    /** Installs the shim that scopes a grid filter popup's value list by the other columns' filters before Syncfusion's row cap. */
    private syncfusionGridFilterScope: SyncfusionGridFilterScopeService,
    /** Installs the shim that removes the column-menu icon from every grid's Action column. */
    private syncfusionGridActionColumn: SyncfusionGridActionColumnService,
  ) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth' // remove if you want instant scroll
          });

          let child = this.activatedRoute.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          return child?.snapshot.data['title'] || this.getRouteName();
        })
      )
      .subscribe(title => {
        if (title.toLowerCase() === 'login') {
          this.titleService.setTitle('Sign in : RegTrack - Products that simplify');
        } else {
          const displayTitle = (title === 'Spni Dashboard' || title === 'Trent Dashboard' || title === 'Department Head Dashboard') ? 'Dashboard' : title;
          this.titleService.setTitle(`${displayTitle} : RegTrack`);
        }
      });
  }

  ngOnInit(): void {
    // A tab left open across this deploy still holds the customization flags under
    // their retired key, written by the previous build. Move them onto the single key
    // the app now uses, before anything reads them.
    migrateLegacyCustomizationFlags();

    this.translateService.setDefaultLang('en');
    this.translateService.use('en');

    // Feed the translated column-menu strings ("Sort Ascending", "Sort Descending",
    // "Columns", ...) into Syncfusion's own catalogue and keep them in step with the
    // language. Syncfusion renders that menu outside Angular's templates, so this is the
    // only way those labels can follow the language switcher. Registered after the
    // TranslateService is configured above so the first pass reads real values.
    this.syncfusionGridLocale.init();

    // Join the managed-tab lifecycle: announce this tab's unload for any tab it
    // opened, and — when this tab was itself opened by the app — watch for its
    // opener disappearing or for a logout command.
    this.managedTabService.init();

    // D9: a stored session token (page reload) has no server policy in memory yet. Read it once, passively: the call never
    // counts as activity and never extends the session. No token or an expired token makes no call (the renewal path handles
    // it). The AuthenticationService constructor already armed its timers; while this read is in flight the 20-minute local
    // default cannot act as a logout timer (AuthenticationService.holdForPolicyRead).
    this.sessionPolicyRead.start();

    // Another tab logged out (the global-logout route posts this): drop this tab's session too.
    // Where it lands is environment-gated inside the service — the portal in production, the
    // in-app login elsewhere — so a development build never bounces out to the UAT portal.
    const logoutChannel = new BroadcastChannel('app-logout');
    logoutChannel.onmessage = (event) => {
      // A tab's own broadcast (tagged with its id) must not log that same tab out a second time.
      if (event.data?.type === 'logout' && event.data?.origin !== LOGOUT_TAB_ID) {
        logoutChannel.close();
        this.globalLogoutService.completeBroadcastLogout();
      }
    };
    // Correct Syncfusion menu and tooltip positioning for the body zoom, then hand body-zoom
    // ownership to the service (initial apply + re-evaluation on 1024px band crossings).
    this.syncfusionMenuZoom.install();
    this.syncfusionTooltipZoom.install();
    this.syncfusionGridDateFilter.install();
    this.syncfusionGridFilterScope.install();
    this.syncfusionGridActionColumn.install();
    this.bodyZoom.init();
    // Keep already-open Syncfusion overlays (dialogs/popups) inside the viewport after
    // a resize — their inline pixel positions are computed once at open time. Registered
    // through Renderer2 so the listener carries a teardown callback (see ngOnDestroy).
    this.removeResizeListener = this.renderer.listen('window', 'resize', () => this.scheduleOverlayReposition());

    // Registered through Renderer2 so the listener carries a teardown callback (see ngOnDestroy). The storage and the
    // path come from the injected document's window, not from browser globals; the exact pathname is kept (not
    // Location.path()) because markPageRefreshing compares it to '/emailVerification'.
    this.removeBeforeUnloadListener = this.renderer.listen('window', 'beforeunload', () => {
      const win = this.document.defaultView;
      if (win) {
        markPageRefreshing(win.sessionStorage, win.location.pathname);
      }
    });

    // Load Tawk widget once on app initialization and ensure it stays minimized
    this.tawkService.load()
      .catch(err => console.error('Failed to load Tawk:', err));

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.showResetLinkLoader = this.shouldShowResetLinkLoader(event.url);
      }

      if (event instanceof NavigationEnd) {
        this.showResetLinkLoader = false;
        this.showContent = !(event.url === '/emailVerification' || event.url === '/');
        this.showPasswordPopup = event.url === '/changePassword';
        this.showSecurityQue = event.url === '/changeSecurityQuestions';
        this.showSSOlogin = event.url === '/ssoLogin';
        this.showResetPassword = event.url === '/resetPassword';
        this.showUnlockAcc = event.url === '/unlockAccount';
        this.showSignIn = event.url === '/signIn';
        this.showOtpVerifyPopup = event.url === '/otpVerification';
        this.showQuestionsToAnswer = event.url === '/securityQuestions';
        this.showOverdueCompliance = event.url === '/overviewOverdueCompliance';
        this.openHelpDesk = event.url === '/helpdesk';
        this.showActSummary = event.url === '/actDetailsSummary';
        this.showLegalUpdateDetails = event.url === '/legalUpdateDetails';
        this.showUpdateDetails = event.url === '/UpdateDetails';
        this.showInternalOverdueCompliance = event.url === '/overviewInternalOverdueCompliance';
        this.showLogin = event.url === '/login';
        this.showAuthRedirection = event.url === '/auth-redirect';
      }

      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.showResetLinkLoader = false;
      }
    });
  }

  /**
   * Tears down the window-resize listener and any pending overlay-reposition debounce
   * when the app shell is destroyed.
   * @returns Nothing.
   */
  ngOnDestroy(): void {
    this.removeResizeListener?.();
    this.removeResizeListener = null;
    this.removeBeforeUnloadListener?.();
    this.removeBeforeUnloadListener = null;
    if (this.overlayRepositionTimer !== null) {
      clearTimeout(this.overlayRepositionTimer);
      this.overlayRepositionTimer = null;
    }
  }

  /**
   * Schedules a debounced repositioning pass for open Syncfusion overlays after a window
   * resize. Debouncing (150ms) avoids doing DOM work on every resize frame.
   */
  private scheduleOverlayReposition(): void {
    if (this.overlayRepositionTimer !== null) {
      clearTimeout(this.overlayRepositionTimer);
    }
    this.overlayRepositionTimer = setTimeout(() => {
      this.overlayRepositionTimer = null;
      this.repositionOpenSyncfusionOverlays();
    }, 150);
  }

  /**
   * Re-centres every currently open Syncfusion dialog so it stays fully visible after a
   * viewport or resolution change. Syncfusion positions dialogs with inline pixel
   * coordinates computed at open time; this calls the public `refreshPosition()` API on
   * each open dialog instance. Failures inside third-party positioning code are swallowed
   * so one misbehaving overlay cannot break the resize pass.
   */
  private repositionOpenSyncfusionOverlays(): void {
    const openDialogs = this.document.querySelectorAll<HTMLElement>('.e-dialog.e-popup-open');
    openDialogs.forEach((element) => {
      // Syncfusion attaches widget instances to the host element; there is no typed API for this.
      const instance = (element as unknown as { ej2_instances?: Array<{ refreshPosition?: () => void }> }).ej2_instances?.[0];
      try {
        instance?.refreshPosition?.();
      } catch {
        /* Third-party positioning failure must not break the app shell. */
      }
    });
  }

  private shouldShowResetLinkLoader(url: string): boolean {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return parsedUrl.pathname === '/changePassword' && parsedUrl.searchParams.has('data');
    } catch {
      return false;
    }
  }

  private getRouteName(): string {
    const routeParts = this.router.url.split('/');
    const lastPart = routeParts[routeParts.length - 1];
    const formattedRouteName = this.addSpaceBeforeCapital(lastPart);
    return formattedRouteName.charAt(0).toUpperCase() + formattedRouteName.slice(1);
  }

  private addSpaceBeforeCapital(routeName: string): string {
    return routeName.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

}

